// Counts the database reads in flight, so the page load's shutter can stay closed while the opened view fetches its data
// and open as soon as it has (or straight away for a view that reads nothing). Pure: no DOM, so the unit tests use it.

type Read<A extends unknown[], R> = (...args: A) => Promise<R>;

export class DbReadActivity {
  private __pending = 0;

  private __waiters: Array<() => void> = [];

  private __timer?: ReturnType<typeof setTimeout>;

  /** @param quietMs how long nothing must be in flight before the reads count as settled */
  constructor(private readonly quietMs = 150) {}

  get pending() {
    return this.__pending;
  }

  /** The read, counted while it is out. Its answer, or its failure, is passed on unchanged. */
  wrap<A extends unknown[], R>(read: Read<A, R>): Read<A, R> {
    return (...args: A) => {
      this.__start();
      let result: Promise<R>;
      try {
        result = read(...args);
      } catch (err) {
        this.__end();
        throw err;
      }
      return result.finally(() => this.__end());
    };
  }

  /** Resolves once no read has been out for quietMs, counting reads that start in the meantime. Never rejects. */
  whenSettled(): Promise<void> {
    return new Promise((resolve) => {
      this.__waiters.push(resolve);
      if (this.__pending === 0) this.__arm();
    });
  }

  private __start() {
    this.__pending += 1;
    if (this.__timer) clearTimeout(this.__timer);
    this.__timer = undefined;
  }

  private __end() {
    this.__pending = Math.max(0, this.__pending - 1);
    if (this.__pending === 0 && this.__waiters.length > 0) this.__arm();
  }

  private __arm() {
    if (this.__timer) clearTimeout(this.__timer);
    this.__timer = setTimeout(() => {
      this.__timer = undefined;
      if (this.__pending !== 0) return;
      const waiters = this.__waiters;
      this.__waiters = [];
      waiters.forEach((resolve) => resolve());
    }, this.quietMs);
  }
}
